import { useState, useEffect, useCallback, useRef } from "react";
import type { Option } from "@/components/ui/multi-select";
import dayjs from "dayjs";

export type SortColumn = "customer" | "dischargeDate" | "specialty" | "writeBackCount";

const toDateString = (date: Date): string => dayjs(date).format("YYYY-MM-DD");

const STORAGE_KEY = "write_back_management_filters";
const DEFAULT_FROM_DATE = "2026-01-01";

interface StoredFilters {
  from: string | null;
  to: string | null;
  customerIds: string[];
  recodesOnly: boolean;
  sortColumn: SortColumn;
  sortDirection: "asc" | "desc";
}

const parseDate = (value: string | null): Date | undefined => {
  if (!value) return undefined;
  const d = dayjs(value);
  return d.isValid() ? d.toDate() : undefined;
};

const loadFromLocalStorage = (): StoredFilters | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as StoredFilters;
  } catch {
    return null;
  }
};

const getUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    from: params.get("from"),
    to: params.get("to"),
    customers: params.get("customers"),
    recodes: params.get("recodes"),
    sortBy: params.get("sortBy"),
    sortDir: params.get("sortDir"),
  };
};

function initializeFilters(): {
  from: Date | undefined;
  to: Date | undefined;
  customerIds: string[];
  recodesOnly: boolean;
  sortColumn: SortColumn;
  sortDirection: "asc" | "desc";
} {
  const url = getUrlParams();
  const stored = loadFromLocalStorage();

  const from = parseDate(url.from) ?? parseDate(stored?.from ?? null) ?? new Date(DEFAULT_FROM_DATE);

  const to = parseDate(url.to) ?? parseDate(stored?.to ?? null);

  const customerIds = url.customers ? url.customers.split(",").filter(Boolean) : (stored?.customerIds ?? []);

  const recodesOnly = url.recodes !== null ? url.recodes === "1" : (stored?.recodesOnly ?? false);

  const validSortColumns: SortColumn[] = ["customer", "dischargeDate", "specialty", "writeBackCount"];
  const rawSortBy = url.sortBy ?? stored?.sortColumn;
  const sortColumn = validSortColumns.includes(rawSortBy as SortColumn) ? (rawSortBy as SortColumn) : "dischargeDate";

  const rawSortDir = url.sortDir ?? stored?.sortDirection;
  const sortDirection: "asc" | "desc" = rawSortDir === "desc" ? "desc" : "asc";

  return { from, to, customerIds, recodesOnly, sortColumn, sortDirection };
}

export function useWriteBackFilters(customerOptions: Option[]) {
  const initial = useRef(initializeFilters()).current;

  const [fromDate, setFromDateState] = useState<Date | undefined>(initial.from);
  const [toDate, setToDateState] = useState<Date | undefined>(initial.to);
  const [selectedCustomers, setSelectedCustomers] = useState<Option[]>([]);
  const [recodesOnly, setRecodesOnly] = useState(initial.recodesOnly);
  const [sortColumn, setSortColumnState] = useState<SortColumn>(initial.sortColumn);
  const [sortDirection, setSortDirectionState] = useState<"asc" | "desc">(initial.sortDirection);

  const hasHydrated = useRef(false);

  // Hydrate customer Options from stored IDs once customerOptions are available
  useEffect(() => {
    if (customerOptions.length === 0 || hasHydrated.current) return;
    if (initial.customerIds.length > 0) {
      const matched = customerOptions.filter((o) => initial.customerIds.includes(o.value));
      if (matched.length > 0) {
        setSelectedCustomers(matched);
      }
    }
    hasHydrated.current = true;
  }, [customerOptions, initial.customerIds]);

  // Persist to localStorage + URL on every change (with hydration guard for customers)
  useEffect(() => {
    // Don't persist until customers have been hydrated (or there are no stored customerIds to hydrate)
    if (!hasHydrated.current && initial.customerIds.length > 0) return;

    const customerIds = selectedCustomers.map((c) => c.value);

    const stored: StoredFilters = {
      from: fromDate ? toDateString(fromDate) : null,
      to: toDate ? toDateString(toDate) : null,
      customerIds,
      recodesOnly,
      sortColumn,
      sortDirection,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));

    // Update URL params
    const params = new URLSearchParams(window.location.search);

    if (fromDate) params.set("from", toDateString(fromDate));
    else params.delete("from");

    if (toDate) params.set("to", toDateString(toDate));
    else params.delete("to");

    if (customerIds.length > 0) params.set("customers", customerIds.join(","));
    else params.delete("customers");

    if (recodesOnly) params.set("recodes", "1");
    else params.delete("recodes");

    params.set("sortBy", sortColumn);
    params.set("sortDir", sortDirection);

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, "", newUrl);
  }, [fromDate, toDate, selectedCustomers, recodesOnly, sortColumn, sortDirection, initial.customerIds.length]);

  const setFromDate = useCallback((date: Date | undefined) => {
    setFromDateState(date);
  }, []);

  const setToDate = useCallback((date: Date | undefined) => {
    setToDateState(date);
  }, []);

  const handleSort = useCallback(
    (column: SortColumn) => {
      if (sortColumn === column) {
        setSortDirectionState((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortColumnState(column);
        setSortDirectionState("asc");
      }
    },
    [sortColumn],
  );

  return {
    fromDate,
    toDate,
    setFromDate,
    setToDate,
    selectedCustomers,
    setSelectedCustomers,
    recodesOnly,
    setRecodesOnly,
    sortColumn,
    sortDirection,
    handleSort,
  };
}
